import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import FilteredSelect from "../../common/components/LocationSelectionStep/FilteredSelect";
import { useKeyHandoverLocations } from "../context/KeyHandoverContext";
import { Spinner } from "../../../../components/ui/spinner";
import { OpeningHours } from "../../../../components/ui/opening-hours";
import { shouldShowOpeningHours } from "../../common/locationSelection/visibility";
import type { RootState } from "../../../../redux/store";

/**
 * KeyHandoverLocationSelectionStep
 * ---------------------------------------------------------------
 * Step component for selecting a location during key handover flow.
 * 
 * @remarks
 * - Reads locations from React Context (via TanStack Query)
 * - Displays location selector with opening hours preview
 * - Handles loading and empty states with proper accessibility
 */
function KeyHandoverLocationSelectionStep() {
	const { t } = useTranslation();
	const locations = useKeyHandoverLocations();
	const selectedLocation = useSelector(
		(state: RootState) => state.bookingCreation.selectedLocation
	);
	const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
	
	// Check if opening hours should be displayed
	const showOpeningHours = useMemo(() => {
		if (!selectedLocation) return false;
		const configAllows = tenantConfig?.showOpeningHoursInBookingFlow ?? true;
		return configAllows && shouldShowOpeningHours(selectedLocation);
	}, [selectedLocation, tenantConfig]);
	
	// Memoize derived state to prevent unnecessary recalculations
	const { isLoading, hasLocations, validLocations } = useMemo(() => {
		const loading = locations === undefined;
		const valid = Array.isArray(locations) ? locations : [];
		const hasLocs = valid.length > 0;
		
		return {
			isLoading: loading,
			hasLocations: hasLocs,
			validLocations: valid,
		};
	}, [locations]);

	return (
		<div
			className="flex flex-col justify-center"
			role="region"
			aria-label={t("book_location.select_location")}
			aria-busy={isLoading}
		>
			{isLoading ? (
				<div className="flex w-full items-center justify-center py-6" aria-live="polite">
					<Spinner size="md" />
				</div>
			) : hasLocations ? (
				<>
					<FilteredSelect
						locations={validLocations}
						triggerClassName="border-black"
						placeholder={t("book_location.select_location_placeholder")}
					/>
					{showOpeningHours && (
						<div className="mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
							<div className="mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
								<OpeningHours location={selectedLocation} showIcon useBookingFlowConfig />
							</div>
						</div>
					)}
				</>
			) : (
				<p className="text-slate-700" aria-live="polite">
					{t("book_location.no_location")}
				</p>
			)}
		</div>
	);
}

export default memo(KeyHandoverLocationSelectionStep);
